import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { proApi, TOKEN_KEY } from "@/lib/proApi";

const ProCtx = createContext(null);

export const ProProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [cart, setCart] = useState(null);
  const [config, setConfig] = useState(null);

  const loadCart = useCallback(async (u) => {
    if (!u || u.status !== "approved") return setCart(null);
    try {
      const { data } = await proApi.get("/pro/cart");
      setCart(data);
    } catch {
      setCart(null);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!localStorage.getItem(TOKEN_KEY)) return setUser(false);
    try {
      const { data } = await proApi.get("/pro/auth/me");
      setUser(data);
      loadCart(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setUser(false);
    }
  }, [loadCart]);

  useEffect(() => {
    refresh();
    proApi.get("/pro/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, [refresh]);

  const signIn = (token, u) => {
    localStorage.setItem(TOKEN_KEY, token);
    setUser(u);
    loadCart(u);
  };
  const signOut = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
    setCart(null);
  };

  const saveItems = async (items) => {
    const { data } = await proApi.put("/pro/cart", { items });
    setCart(data);
    return data;
  };
  const items = () => (cart?.lines || []).map((l) => ({ product_id: l.product_id, pack_size: l.pack_size, lots: l.lots }));
  const addItem = (product_id, pack_size, lots) => saveItems([...items(), { product_id, pack_size, lots }]);
  const setLots = (product_id, pack_size, lots) =>
    saveItems(items().map((i) => (i.product_id === product_id && i.pack_size === pack_size ? { ...i, lots } : i)));
  const removeItem = (product_id, pack_size) =>
    saveItems(items().filter((i) => !(i.product_id === product_id && i.pack_size === pack_size)));

  const count = (cart?.lines || []).reduce((n, l) => n + l.lots, 0);
  const approved = !!user && user.status === "approved";

  return (
    <ProCtx.Provider value={{ user, setUser, cart, setCart, config, approved, count, refresh, signIn, signOut,
      addItem, setLots, removeItem, loadCart }}>
      {children}
    </ProCtx.Provider>
  );
};

export const usePro = () => useContext(ProCtx);
